import React, { useMemo, memo } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,

} from 'recharts';
import { 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Typography, 
  Paper,
  FormControlLabel,
  Switch,
  Chip
} from '@mui/material';
import { ProfitLossData, DateRange } from '../../../../../backend/src/types';

export interface ProfitLossChartProps {
  data: ProfitLossData[];
  showProjections?: boolean;
  comparisonPeriod?: DateRange;
  height?: number;
  showControls?: boolean;
  onShowProjectionsChange?: (show: boolean) => void;
}

interface EnhancedProfitLossData extends ProfitLossData {
  formattedDate: string;
  isProjection?: boolean;
  trend?: 'up' | 'down' | 'stable';
  movingAverage?: number;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const calculateMovingAverage = (data: ProfitLossData[], windowSize: number = 7): number[] => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }
  
  const movingAverages: number[] = [];
  
  for (let i = 0; i < data.length; i++) {
    const start = Math.max(0, i - windowSize + 1);
    const window = data.slice(start, i + 1);
    const average = window.reduce((sum, item) => sum + item.profit, 0) / window.length;
    movingAverages.push(average);
  }
  
  return movingAverages;
};

const generateProjections = (data: ProfitLossData[], days: number = 30): EnhancedProfitLossData[] => {
  if (!data || !Array.isArray(data) || data.length < 7) return []; // Need at least a week of data for projections
  
  // Calculate trend from last 14 days
  const recentData = data.slice(-14);
  const avgRevenue = recentData.reduce((sum, item) => sum + item.revenue, 0) / recentData.length;
  const avgExpenses = recentData.reduce((sum, item) => sum + item.expenses, 0) / recentData.length;
  const avgProfit = recentData.reduce((sum, item) => sum + item.profit, 0) / recentData.length;
  
  // Calculate growth rate
  const firstHalf = recentData.slice(0, 7);
  const secondHalf = recentData.slice(7);
  const firstHalfAvg = firstHalf.reduce((sum, item) => sum + item.profit, 0) / firstHalf.length;
  const secondHalfAvg = secondHalf.reduce((sum, item) => sum + item.profit, 0) / secondHalf.length;
  const growthRate = firstHalfAvg !== 0 ? (secondHalfAvg - firstHalfAvg) / firstHalfAvg : 0;
  
  const projections: EnhancedProfitLossData[] = [];
  const lastDate = new Date(data[data.length - 1].date);
  
  for (let i = 1; i <= days; i++) {
    const projectionDate = new Date(lastDate);
    projectionDate.setDate(lastDate.getDate() + i);
    
    // Add some randomness to make projections more realistic
    const randomFactor = 0.9 + Math.random() * 0.2; // ±10% variation
    const trendFactor = 1 + (growthRate * i / days);
    
    const projectedRevenue = avgRevenue * trendFactor * randomFactor;
    const projectedExpenses = avgExpenses * (0.95 + Math.random() * 0.1); // Expenses more stable
    const projectedProfit = projectedRevenue - projectedExpenses;
    
    projections.push({
      date: projectionDate.toISOString().split('T')[0],
      revenue: projectedRevenue,
      expenses: projectedExpenses,
      profit: projectedProfit,
      profitMargin: projectedRevenue > 0 ? (projectedProfit / projectedRevenue) * 100 : 0,
      formattedDate: formatDate(projectionDate.toISOString().split('T')[0]),
      isProjection: true,
    });
  }
  
  return projections;
};

const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload as EnhancedProfitLossData;
    
    return (
      <Paper sx={{ p: 2, maxWidth: 300 }}>
        <Typography variant="subtitle2" gutterBottom>
          {data.formattedDate}
          {data.isProjection && (
            <Chip 
              label="Projection" 
              size="small" 
              color="warning" 
              sx={{ ml: 1 }} 
            />
          )}
        </Typography>
        <Typography variant="body2" color="success.main">
          Revenue: {formatCurrency(data.revenue)}
        </Typography>
        <Typography variant="body2" color="error.main">
          Expenses: {formatCurrency(data.expenses)}
        </Typography>
        <Typography 
          variant="body2" 
          color={data.profit >= 0 ? 'success.main' : 'error.main'}
          fontWeight="bold"
        >
          Profit: {formatCurrency(data.profit)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Margin: {data.profitMargin.toFixed(1)}%
        </Typography>
        {data.movingAverage !== undefined && (
          <Typography variant="body2" color="text.secondary">
            7-day avg: {formatCurrency(data.movingAverage)}
          </Typography>
        )}
      </Paper>
    );
  }

  return null;
};

export const ProfitLossChart: React.FC<ProfitLossChartProps> = memo(({
  data,
  showProjections = false,
  comparisonPeriod,
  height = 400,
  showControls = true,
  onShowProjectionsChange,
}) => {
  const chartData = useMemo(() => {
    // Handle undefined or empty data
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    // Calculate moving averages
    const movingAverages = calculateMovingAverage(data);
    
    // Enhance data with additional information
    const enhancedData: EnhancedProfitLossData[] = data.map((item, index) => {
      // Determine trend
      let trend: 'up' | 'down' | 'stable' = 'stable';
      if (index > 0) {
        const prevProfit = data[index - 1].profit;
        const currentProfit = item.profit;
        const change = Math.abs(currentProfit - prevProfit);
        const threshold = Math.abs(prevProfit) * 0.05; // 5% threshold
        
        if (change > threshold) {
          trend = currentProfit > prevProfit ? 'up' : 'down';
        }
      }
      
      return {
        ...item,
        formattedDate: formatDate(item.date),
        trend,
        movingAverage: movingAverages[index],
        isProjection: false,
      };
    });
    
    // Add projections if enabled
    if (showProjections) {
      const projections = generateProjections(data);
      return [...enhancedData, ...projections];
    }
    
    return enhancedData;
  }, [data, showProjections]);

  const actualData = chartData.filter(item => !item.isProjection);
  const projectionData = chartData.filter(item => item.isProjection);
  
  const totalRevenue = actualData && actualData.length > 0 
    ? actualData.reduce((sum, item) => sum + item.revenue, 0) 
    : 0;
  const totalExpenses = actualData && actualData.length > 0 
    ? actualData.reduce((sum, item) => sum + item.expenses, 0) 
    : 0;
  const totalProfit = totalRevenue - totalExpenses;
  const avgProfitMargin = actualData && actualData.length > 0 
    ? actualData.reduce((sum, item) => sum + item.profitMargin, 0) / actualData.length 
    : 0;

  const profitableDays = actualData.filter(item => item.profit > 0).length;
  const profitabilityRate = actualData.length > 0 ? (profitableDays / actualData.length) * 100 : 0;

  // Handle no data case
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height }}>
        <Typography variant="body2" color="text.secondary">
          No profit/loss data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {showControls && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showProjections}
                onChange={(e) => onShowProjectionsChange?.(e.target.checked)}
              />
            }
            label="Show Projections"
          />
          
          <Box sx={{ ml: 'auto', textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              Total Profit: {formatCurrency(totalProfit)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Avg Margin: {avgProfitMargin.toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Profitable Days: {profitabilityRate.toFixed(0)}%
            </Typography>
          </Box>
        </Box>
      )}
      
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="formattedDate" 
            tick={{ fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis 
            tickFormatter={formatCurrency}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          
          {/* Zero line for profit/loss reference */}
          <ReferenceLine y={0} stroke="#666" strokeDasharray="2 2" />
          
          {/* Revenue and Expenses as bars */}
          <Bar 
            dataKey="revenue" 
            fill="#4caf50" 
            name="Revenue"
            fillOpacity={0.7}
          />
          <Bar 
            dataKey="expenses" 
            fill="#f44336" 
            name="Expenses"
            fillOpacity={0.7}
          />
          
          {/* Profit as line */}
          <Line
            type="monotone"
            dataKey="profit"
            stroke="#1976d2"
            strokeWidth={3}
            dot={(props: any) => {
              const { payload } = props;
              return (
                <circle
                  {...props}
                  fill={payload.isProjection ? '#ff9800' : '#1976d2'}
                  stroke={payload.isProjection ? '#ff9800' : '#1976d2'}
                  strokeWidth={2}
                  r={payload.isProjection ? 3 : 4}
                  strokeDasharray={payload.isProjection ? '3 3' : 'none'}
                />
              );
            }}
            strokeDasharray="5 5"
            name="Profit"
          />
          
          {/* Moving average line */}
          <Line
            type="monotone"
            dataKey="movingAverage"
            stroke="#9c27b0"
            strokeWidth={2}
            strokeDasharray="3 3"
            dot={false}
            name="7-day Average"
          />
        </ComposedChart>
      </ResponsiveContainer>
      
      {showProjections && projectionData.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="warning.main">
            * Projections are estimates based on recent trends and may not reflect actual future performance
          </Typography>
        </Box>
      )}
    </Box>
  );
});